import {useMemo} from 'react';
import {ChartData} from '@/providers/overview/OverviewProvider';
import {PriceHistoryData} from '@/providers/overview/usePriceHistories';
import {ETH_ADDRESS} from '@/util/chains';

export function useChartData(priceHistories?: PriceHistoryData[]) {
  return useMemo((): ChartData => {
    return Object.fromEntries(
      priceHistories?.flatMap(item => {
        const data = createChartDataItem(item);
        return [
          item.id,
          ...Object.values(item.detail_platforms ?? {}).map(
            object => object.contract_address?.toLowerCase() || ETH_ADDRESS
          ),
        ].map(key => [key, data]);
      }) || []
    );
  }, [priceHistories]);
}

function createChartDataItem(item: PriceHistoryData): ChartData['key'] {
  const tick = item.priceHistory.interval === 'hourly' ? 60 * 60 : 24 * 60 * 60;
  return {
    image: item.image.small,
    color: item.color,
    timeSeries: createTimeSeriesData(item),
    name: item.name,
    symbol: item.symbol,
    record: item.priceHistory.history.reduce(
      (obj, currentValue) => {
        obj[currentValue.timestamp.toString()] = +currentValue.price;
        return obj;
      },
      {} as Record<string, number>
    ),
    tick,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getPrice(timestamp?: number, defaultPrice?: number): number | undefined {
      if (!timestamp) {
        return (
          this.timeSeries[this.timeSeries.length - 1]?.value || defaultPrice
        );
      }
      const earliestPrice = this.timeSeries[0];

      if (!earliestPrice) {
        return defaultPrice;
      }

      if (earliestPrice.timestamp >= timestamp) {
        return earliestPrice.value;
      }

      const interval = BigInt(this.tick);
      return (
        this.record[((BigInt(timestamp) / interval) * interval).toString()] ||
        defaultPrice
      );
    },
    getTimestampFromLast(ticksAgo: number): number {
      return this.timeSeries.length > 0
        ? this.timeSeries[this.timeSeries.length - 1]?.timestamp -
            this.tick * ticksAgo
        : 0;
    },
  };
}

function createTimeSeriesData(item: PriceHistoryData) {
  return item.priceHistory.history.map(item => ({
    timestamp: item.timestamp,
    value: +item.price,
  }));
}
